import { FilterOperator, IConditionLeaf, IRuleCondition, OPERATORS, RuleCombinator } from "../model/vector-style-condition.model";

/** Operator vocabulary this module understands as a leaf comparison — derived from OPERATORS so the two can't drift. */
export const COMPARISON_OPS: ReadonlySet<string> = new Set(OPERATORS.map((op) => op.value));

/** Flips a comparison operator when the "get" expression and the literal swap sides (`5 > x` becomes `x < 5`). */
const FLIP_OPERATOR: Partial<Record<FilterOperator, FilterOperator>> = {
  ">": "<",
  "<": ">",
  ">=": "<=",
  "<=": ">=",
  "==": "==",
  "!=": "!=",
};

/** Strips an optional `["to-string", expr]` wrapper (used to coerce non-string property values for OL's `text-value`, which requires a string) before inspecting the inner expression. */
function unwrapToString(value: unknown): unknown {
  return Array.isArray(value) && value[0] === "to-string" && value.length === 2 ? value[1] : value;
}

function isGetExpression(value: unknown): value is unknown[] {
  const inner = unwrapToString(value);
  return Array.isArray(inner) && inner[0] === "get" && inner.slice(1).every((p) => typeof p === "string");
}

function getExpressionPath(getExpr: unknown[]): string[] {
  return (unwrapToString(getExpr) as unknown[]).slice(1) as string[];
}

function isLiteral(value: unknown): value is string | number | boolean {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

/** Unwraps a `["get", ...path]` expression into its path segments, or undefined if `value` isn't one — shared by every "is this a get-expression" call site (the dialog's text-property patch, the legend's label-property hint). */
export function unwrapGetExpression(value: unknown): string[] | undefined {
  return isGetExpression(value) ? getExpressionPath(value) : undefined;
}

/**
 * Parses one leaf filter into a `{propertyPath, operator, value}` triple. Accepts a simple
 * comparison in either order (`[op, get, literal]` or `[op, literal, get]`, flipping the operator
 * for the latter — same both-orders leniency `coerceRuleFilters` already has in
 * geojson-style-converter.ts) and OL's membership form (`["in"|"!in", get, ["literal", [...]]]`).
 * Returns null for anything else (nested compounds, `match`/`case`/`between`, a literal `null`, ...) —
 * callers fall back to preserving the original filter verbatim rather than guessing.
 */
export function parseLeafTriple(filter: unknown): IConditionLeaf | null {
  if (!Array.isArray(filter) || filter.length !== 3) return null;
  const [rawOp, a, b] = filter;

  if ((rawOp === "in" || rawOp === "!in") && isGetExpression(a) && Array.isArray(b) && b[0] === "literal" && Array.isArray(b[1])) {
    return { propertyPath: getExpressionPath(a), operator: rawOp === "in" ? "in" : "not in", value: b[1] as (string | number)[] };
  }

  if (typeof rawOp !== "string" || !COMPARISON_OPS.has(rawOp)) return null;
  const op = rawOp as FilterOperator;
  if (op === "in" || op === "not in") return null; // membership without the "literal" wrapper isn't understood here

  const aIsGet = isGetExpression(a);
  const bIsGet = isGetExpression(b);
  if (aIsGet === bIsGet) return null; // need exactly one side to be a "get" expression

  if (aIsGet) {
    if (!isLiteral(b)) return null;
    return { propertyPath: getExpressionPath(a), operator: op, value: b };
  }

  if (!isLiteral(a)) return null;
  const flipped = FLIP_OPERATOR[op];
  if (!flipped) return null;
  return { propertyPath: getExpressionPath(b as unknown[]), operator: flipped, value: a };
}

/** Serializes one leaf back to its canonical (property on the left) wire-format triple. */
export function leafToTriple(leaf: IConditionLeaf): unknown[] {
  const getExpr = ["get", ...leaf.propertyPath];
  if (leaf.operator === "in" || leaf.operator === "not in") {
    return [leaf.operator === "in" ? "in" : "!in", getExpr, ["literal", leaf.value]];
  }
  return [leaf.operator, getExpr, leaf.value];
}

/**
 * Flattens a filter into its top-level leaf triples: `[filter]` for a simple triple, `filter.slice(1)`
 * for a compound `all`/`any`, `[]` for anything else. Shared between {@link filterToCondition} and
 * `deriveMetadataFromRules` in geojson-style-converter.ts so both understand compound filters identically.
 */
export function flattenSimpleFilter(filter: unknown): unknown[] {
  if (!Array.isArray(filter)) return [];
  if (filter[0] === "all" || filter[0] === "any") return filter.slice(1);
  return [filter];
}

/**
 * Parses a wire-format filter into the UI's internal condition model. A filter that doesn't fit
 * "flat combinator + simple leaves" (genuine nested compounds, `match`/`case`/`between`, ...) comes
 * back as `{conditions: [], unsupported: true, raw: filter}` — the caller must render it read-only
 * and preserve `raw` verbatim rather than losing or corrupting it.
 */
export function filterToCondition(filter: unknown[] | undefined | null): IRuleCondition {
  if (filter === undefined || filter === null) return { combinator: "all", conditions: [] };

  const isCompound = Array.isArray(filter) && (filter[0] === "all" || filter[0] === "any");
  const combinator: RuleCombinator = isCompound ? (filter[0] as RuleCombinator) : "all";
  const parts = flattenSimpleFilter(filter);

  if (parts.length === 0) {
    return { combinator: "all", conditions: [], unsupported: true, raw: filter };
  }

  const leaves: IConditionLeaf[] = [];
  for (const part of parts) {
    const leaf = parseLeafTriple(part);
    if (!leaf) return { combinator: "all", conditions: [], unsupported: true, raw: filter };
    leaves.push(leaf);
  }

  return { combinator, conditions: leaves };
}

/** Serializes the UI's internal condition model back to a wire-format filter, or `undefined` when empty. */
export function conditionToFilter(condition: IRuleCondition): unknown[] | undefined {
  if (condition.unsupported && condition.raw) return condition.raw;
  if (condition.conditions.length === 0) return undefined;
  if (condition.conditions.length === 1) return leafToTriple(condition.conditions[0]);
  return [condition.combinator, ...condition.conditions.map(leafToTriple)];
}
